/**
 * What a cue from beat 4 *means*, in a corridor made of triangles.
 *
 * Same seam as `bornDirector.ts`, `latchDirector.ts` and `ottoDirector.ts`:
 * `story/act1/serviceCorridor.ts` says "the handler comes down the short leg and
 * the dog is with him", and this file is the only thing that knows the handler is
 * a `Worker` on an authored route, that the dog has a neck pivot which puts its
 * nose on the floor, and that the door has a hinge.
 *
 * ── This is the first director that is not purely presentation ──────────────
 *
 * Beat 3's header says its whole file runs on the render step because nothing in
 * the simulation could see Otto. **Beat 4 is the opposite case and it is the
 * reason this class has two entry points.**
 *
 *  - `step(dt, …)` runs in `fixedUpdate`, and everything the stealth system, the
 *    scent system or a collider can observe changes there and only there: the
 *    three patrols, the dog's position, the door's angle, and the wet box. D-044
 *    in one line — *anything the stealth system reads advances in the fixed
 *    step* — and beat 2's `latchDirector.ts` already had to keep its gate's open
 *    fraction for the same reason.
 *  - `update(frameDt)` runs on the render step and moves meshes to match
 *    decisions that have already been made. Nothing here may write a value that
 *    `step` reads back.
 *
 * If a later session finds itself wanting to move the dog in `update`, the thing
 * to check first is that nothing has started asking where the dog *is*.
 *
 * ── The dog's four states are a hand-written timeline, not clips ─────────────
 *
 * Same argument `ottoDirector.ts` makes about the demonstration. Clips in
 * `src/anim/` are `Pose` deltas addressed by name against Rosalie's rig (D-015),
 * driven by gait and speed, and reusable. This is four states, in one beat, for
 * one animal, and the shape of it *is* the writing: how long the dog walks before
 * it stops, and how long it stands there before the nose goes down. Behind a clip
 * API those numbers would be the hardest ones in the file to find.
 */

import * as THREE from 'three';
import { applyDogPose } from '@/anim/dogPose';
import { CORRIDOR_CUES } from '@/story/act1/serviceCorridor';
import { Patrol } from '@/systems/patrol';
import { ScentTrail, Tracker, type Aabb, type TrackerState } from '@/systems/scent';
import type { StealthSnapshot } from '@/systems/stealth';
import { dogAsset, findDogParts, type DogParts } from '@/models/dog';
import { findWorkerParts, type WorkerParts } from '@/models/env/worker';
import { getAsset } from '@/models/registry';
import { disposeAsset, surface } from '@/models/materials';
import { getZone } from '@/render/palette';
import { FadeOverlay } from '@/world/fadeOverlay';
import { DOOR_AT, MAT_AT, SERVICE_SPAWN } from '@/world/serviceCorridor';
import { CHECKER, DOG, DOOR, HANDLER, WALKER, WATER } from '@/world/corridorScene';
import type { Cue } from '@/story/types';

/** The dog's four states, and there are deliberately no others (`docs/beat-4-plan.md`). */
type DogPose = 'heel' | 'stop' | 'nose-down' | 'hunt';

type CorridorCueId = (typeof CORRIDOR_CUES)[keyof typeof CORRIDOR_CUES];

/**
 * When the dog stops, measured from the handler reaching his station.
 *
 * ── This used to be a wall clock and it did not work ─────────────────────────
 *
 * The first version ran a timeline from the cue: heel for 6.5 s, stand, nose down
 * at 7.2 s. Nothing in it knew where the handler was, so the dog stopped wherever
 * the seventh second happened to catch it. Watched for the first time in session
 * 22, that turned out to be **(12.0, 8.6) — through the personnel door, 13.9 m
 * from her and around a corner** — and by then the handler had parked, which hid
 * the dog outright. The beat's one important frame played to an empty corridor
 * for nine seconds and every test passed.
 *
 * So the stop is now hung on the thing the plan actually describes: *the handler
 * comes down the short leg, it passes, and then it stops **at the place she
 * crossed***. His route's third waypoint is (0, 5.2), level with the wash-down
 * bay's mouth and facing away from it, and his dwell there is the whole staging.
 * The dog walks half a step past him and stands, which puts its nose about a
 * metre outside the cover she is standing in.
 *
 * **Move the handler's waypoint and this moves with it.** That is the point.
 */
/**
 * The leaf's answer to her weight on the mat (`#stepDoor`). Two degrees at
 * most — 3.5 cm at the leaf's edge — so it reads as a latch taking up its
 * slack and never as the door starting to open. `again` keeps a pig standing
 * at the mat's edge from turning it into a rattle.
 */
const MAT_SHIVER = { angle: 0.035, rate: 38, decay: 7, seconds: 0.6, again: 1.5 } as const;

const ARRIVAL = {
  /** Index into `handlerRoute()`'s waypoints: the dwell beside the wash-down bay. */
  station: 2,
  /** How long it keeps walking after he stops. A half step, so it is not a freeze-frame. */
  walksOnFor: 0.8,
  /** And then the head goes down. Short: a dog reads a floor the moment it stands still. */
  noseDownAfter: 0.7,
} as const;

/**
 * The cast and the gait now live in `src/anim/dogPose.ts`, with the paragraph
 * that derived them.
 *
 * They were authored here, for the frame this beat is built toward — deliberately
 * larger and slower than a real dog's, because a subtle cast is indistinguishable
 * from an idle animation at the distance the player is standing. Beat 7 copied
 * them; beat 10 would have been the third copy, so the arithmetic moved out and
 * this director now says only *what its dog is doing*.
 */
const PATROL_DEFINITIONS = [WALKER, CHECKER, HANDLER] as const;

/**
 * Capture, as a timeline.
 *
 * `FadeOverlay` approaches its target exponentially at a fixed rate rather than
 * taking an authored number of seconds, so these are the elapsed times at which
 * this class acts, not the durations of the fades themselves. Matching the plan's
 * 0.9 / 1.6 / 1.2 exactly would mean giving the shared overlay a per-caller
 * speed, and beat 4 is not the beat to start reshaping the component every other
 * beat already depends on.
 */
const CAPTURE = {
  /** Black, and long enough to be a cut rather than a stumble. */
  blackAt: 2.5,
  /** How long the fade back in is allowed before she is hers again. */
  backAt: 1.2,
  /** Below this the screen is not black enough for a second capture to be fair. */
  fadeFloor: 0.1,
} as const;

/**
 * Which stages lay a trail.
 *
 * **Not the crossing.** A dog that arrives to a trail covering the whole of stage
 * 4 would make stage 6 a formality — it would have eighteen metres of evidence
 * and she would have one drain. The trail starts when the dog does.
 */
const TRAIL_STAGES: readonly string[] = ['the-dog', 'the-nose', 'wash-it-off', 'the-door', 'close'];

export interface CorridorDirectorOptions {
  /** `observe` comes alive on the patrols. */
  onOfferPatrol(): void;
  /** `nudge` comes alive on the nozzle. */
  onOfferNozzle(): void;
  /**
   * A paw lands, and where.
   *
   * Same shape as `OttoDirector`'s `onThump`: the director knows an animal moved
   * and refuses to know what that sounds like, because `main.ts` is the only file
   * allowed to hold the audio engine and the world in the same hand.
   *
   * **In this beat the sound is the mechanic.** She is around a corner from the
   * dog for most of stage 6 and claws on sealed concrete are the only thing that
   * tells her where it is — spec §8 makes emission gameplay, and a silent hunter
   * in a corridor with no dark is an unexplainable detection waiting to happen.
   */
  onDogStep(at: { x: number; y: number; z: number }): void;
  /** It snuffs at the floor. The sound that says *it has found where you were*. */
  onDogCast(at: { x: number; y: number; z: number }): void;
  /**
   * She stepped onto the mat and it did not open the door: the latch knocks
   * in its keeper and the leaf shivers. Optional; the leaf moves either way.
   */
  onMatTry?(at: { x: number; y: number; z: number }): void;
  /**
   * A cue the DIRECTOR emits, rather than one it consumes.
   *
   * There is exactly one — `dragged` — and it goes back through the beat's own
   * cue channel rather than being handled here privately, so capture appears in
   * the same trace as everything else that happened. A cue a director invents
   * for itself is a cue nothing can log or diff.
   */
  onCue(cue: CorridorCueId): void;
}

/** `window.__rosalie.corridorDebug`, routed from beat 4's part `diagnostics()`. */
export interface CorridorDebug {
  readonly patrols: readonly { id: string; x: number; z: number; facing: number; phase: string; roundsCompleted: number }[];
  readonly tracker: { x: number; z: number; facing: number; mode: string; lostSeconds: number };
  /**
   * The dog's real pose and position, which `tracker` only knows during a hunt.
   * Stage 5 happens entirely outside the tracker; see `CorridorDirector.dogDebug`.
   */
  readonly dog: { x: number; z: number; visible: boolean; pose: string };
  readonly doorAngle: number;
  /** Times the mat answered her weight without opening (audit row 12). */
  readonly matTries: number;
  readonly wetBox: unknown;
  readonly stealth: unknown;
}

export class CorridorDirector {
  /**
   * The three patrols, in the order `corridorScene.ts` declares them.
   *
   * Index is meaningful and is used below: 0 is the walker (the metronome), 1 the
   * checker (the corner), 2 the handler (the dog, and the door). Named accessors
   * would read better and would also let a reorder in the scene file silently
   * repoint them, so the order is asserted by this comment and by the ids.
   */
  readonly patrols = [new Patrol(WALKER), new Patrol(CHECKER), new Patrol(HANDLER)] as const;

  readonly #workerMeshes: readonly THREE.Object3D[];
  readonly #workerParts: readonly WorkerParts[];
  readonly #dog: THREE.Object3D;
  readonly #dogParts: DogParts;
  readonly #doorLeaf: THREE.Object3D;
  readonly #doorHinge: THREE.Object3D;
  readonly #doorPanel: THREE.Object3D;
  readonly #wetPatch: THREE.Mesh;
  readonly #tracker: Tracker;
  readonly #trail = new ScentTrail();
  readonly #fadeOverlay = new FadeOverlay();
  readonly #options: CorridorDirectorOptions;

  #dogPose: DogPose = 'heel';
  /**
   * Always advancing, and separate from `#arrival` on purpose.
   *
   * The first version drove the leg swing from the arrival timer, which is null
   * until the handler is cued in — so the dog stood with frozen legs through the
   * whole of its walk-on, and then again through the entire hunt once the arrival
   * timeline had run out. A gait clock is not a stage clock.
   */
  #gaitClock = 0;
  // Presentation-only gait state. Distance, not elapsed time, keeps a foot's
  // pose aligned with the fixed-step stride that emitted its bootfall.
  readonly #workerDistance = [0, 0, 0];
  readonly #workerPrevious: ({ x: number; z: number } | null)[] = [null, null, null];
  /** Whether stage 5 has cued the dog in at all. Nothing before this puts it on screen. */
  #dogOnStage = false;
  /**
   * Where the arrival staging is up to, which is **not** the same as whether the
   * dog is in the room.
   *
   * `waiting` — armed, and the next time the handler reaches his station the dog
   * comes off his heel. `done` — the handler has whistled it back and the
   * staging must never run again, even though the dog is still walking around
   * the corridor at his heel for the rest of the beat.
   *
   * Capture deliberately leaves this `waiting`: being caught does not delete the
   * dog from the beat (spec §5.6), so the staging replays on his next round.
   */
  #staging: 'waiting' | 'done' = 'waiting';
  /** Seconds since the handler reached `ARRIVAL.station`, or null while he is still walking. */
  #atStation: number | null = null;
  /** Latched the first time the nose goes down. The beat waits on this, not on a clock. */
  #noseDown = false;
  /** Metres walked since the last claw tick, and seconds since the last snuff. */
  #stepDistance = 0;
  #sinceCast = 0;
  #dogPrevious: { x: number; z: number } | null = null;
  /** Seconds since the valve opened, or null before it. */
  #water: number | null = null;
  #doorAngle = 0;
  #matHold = 0;
  /** Whether she was on the mat last step: the answer is on the step she arrives, not every step she stays. */
  #herOnMat = false;
  /** Seconds since the mat last answered her weight, or null before it ever has. */
  #sinceMatTry: number | null = null;
  #matTries = 0;
  #wetBox: Aabb | null = null;
  #trailBroken = false;
  #captureStage: 'out' | 'in' | null = null;
  #captureElapsed = 0;
  #captureRespawn: ((spawn: typeof SERVICE_SPAWN) => void) | null = null;

  constructor(scene: THREE.Scene, options: CorridorDirectorOptions) {
    this.#options = options;

    const workerAsset = getAsset('worker');
    if (!workerAsset) throw new Error('corridor director: the worker asset is not registered');
    // Three copies share one anonymous construction: the corridor needs men who
    // read as human at following-camera distance without asking who any is.
    this.#workerMeshes = this.patrols.map(() => workerAsset.create());
    this.#workerParts = this.#workerMeshes.map((mesh) => findWorkerParts(mesh));
    for (const mesh of this.#workerMeshes) scene.add(mesh);

    this.#dog = dogAsset.create();
    this.#dogParts = findDogParts(this.#dog);
    // Hidden until the handler is on stage. Without this the dog stands in the
    // corridor from the first frame of the beat, at its handler's off-stage park
    // position, which is through the personnel door and inside a wall.
    this.#dog.visible = false;
    scene.add(this.#dog);

    const doorAsset = getAsset('personnel-door');
    if (!doorAsset) throw new Error('corridor director: the personnel-door asset is not registered');
    // A second copy of a door the room's dressing already places, and `main.ts`
    // hides the dressed one when this beat runs. The swap is worth one draw call:
    // `instanceGroup` merges its contents into static geometry (D-024), so the
    // dressed door has no hinge left to turn.
    this.#doorLeaf = doorAsset.create();
    this.#doorLeaf.position.set(12.0, 0, 9.0);
    this.#doorLeaf.rotation.y = -Math.PI / 2;
    const hinge = this.#doorLeaf.getObjectByName('door-hinge');
    const panel = this.#doorLeaf.getObjectByName('steel-leaf-panel');
    if (!hinge || !panel) throw new Error('corridor director: the personnel door has no rigged leaf');
    this.#doorHinge = hinge;
    this.#doorPanel = panel;
    scene.add(this.#doorLeaf);

    const zone = getZone('service');
    const wetColour = zone?.surfaces[0];
    if (wetColour === undefined) throw new Error('corridor director: the service palette has no darkest surface');
    // Wet concrete is darker concrete, so the patch is painted in the zone's own
    // darkest surface rather than in a colour invented for water. Anything
    // blue-tinted here would read as a hole in the floor, and the room is already
    // the first cold-keyed space in the game.
    this.#wetPatch = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), surface('mineral', wetColour));
    this.#wetPatch.rotation.x = -Math.PI / 2;
    this.#wetPatch.position.y = 0.005;
    this.#wetPatch.visible = false;
    scene.add(this.#wetPatch);

    this.#tracker = new Tracker({
      speed: DOG.speed,
      turnRate: DOG.turnRate,
      giveUpSeconds: DOG.giveUpSeconds,
      start: { x: HANDLER.route.park.x, y: 0, z: HANDLER.route.park.z },
    });
  }

  get fade(): number {
    return this.#fadeOverlay.value;
  }

  get doorAngle(): number {
    return this.#doorAngle;
  }

  /** Times the mat has answered her weight without opening (audit row 12). */
  get matTries(): number {
    return this.#matTries;
  }

  get wetBox(): Aabb | null {
    return this.#wetBox;
  }

  get trailIsBroken(): boolean {
    return this.#trailBroken;
  }

  /**
   * The dog has stopped where she crossed and put its nose down.
   *
   * Latched, and the beat's stage 5 ends on it rather than on a duration. A
   * held stage cannot know whether the thing it was holding for happened —
   * which is precisely how the old version ran its full eleven seconds over an
   * empty corridor and reported success.
   */
  get noseIsDown(): boolean {
    return this.#noseDown;
  }

  get dogState(): Readonly<TrackerState> {
    return this.#tracker.state;
  }

  /**
   * Where the dog actually is, and what it is doing.
   *
   * **Not the same thing as `dogState`**, and the difference is the whole reason
   * this exists: the `Tracker` only owns the animal while it is hunting, so
   * during `heel`, `stop` and `nose-down` its state is a stale position from
   * before the hunt began. Every frame of stage 5 — the one stage the beat is
   * built toward — is therefore invisible to anything reading `dogState`, which
   * is how the back half came to be shipped without a single test able to say
   * where the dog was standing.
   */
  get dogDebug(): { x: number; z: number; visible: boolean; pose: DogPose } {
    return {
      x: this.#dog.position.x,
      z: this.#dog.position.z,
      visible: this.#dog.visible,
      pose: this.#dogPose,
    };
  }

  /** Where the door's solid actually is now, for the collider that follows it. */
  get doorColliderPose(): { position: THREE.Vector3; quaternion: THREE.Quaternion } {
    this.#doorLeaf.updateMatrixWorld(true);
    return {
      position: this.#doorPanel.getWorldPosition(new THREE.Vector3()),
      quaternion: this.#doorPanel.getWorldQuaternion(new THREE.Quaternion()),
    };
  }

  handle(cue: Cue): void {
    switch (cue.id) {
      case CORRIDOR_CUES.open:
        this.#fadeOverlay.snapToBlackAndFadeIn();
        break;
      // The walker alone. The teach works because there is only one thing moving
      // and the junction bay is four metres away and unlit.
      case CORRIDOR_CUES.firstPatrol:
        this.patrols[0].start();
        break;
      case CORRIDOR_CUES.clear:
        break;
      case CORRIDOR_CUES.offerPatrol:
        this.#options.onOfferPatrol();
        break;
      // And now all three. This is the crossing.
      case CORRIDOR_CUES.crossing:
        this.patrols[1].start();
        this.patrols[2].start();
        break;
      // ── The handler comes down the short leg, and the dog is with him ──────
      //
      // **Only a resting handler is restarted, and the asymmetry is the fix.**
      // This was `interrupt()` then `start()`, which reads as "put him back to
      // the top of his round" and is not what either method does: `start()`
      // returns immediately once a worker has begun, so all that survived was
      // the interrupt — and an interrupt sends him *out of the room*. Stage 5
      // played as the handler leaving with the dog trotting after him.
      //
      // A man already walking his round is left exactly where he is. He is on
      // his way to the wash-down bay anyway, and `restart()` snaps a position:
      // used on him it would teleport a lit, watched figure. Used on a parked
      // one it cannot be seen, because his park is beyond the personnel door.
      case CORRIDOR_CUES.handlerArrives:
        this.patrols[2].restart();
        this.#dogOnStage = true;
        this.#atStation = null;
        this.#dogPose = 'heel';
        break;
      case CORRIDOR_CUES.looseTheDog:
        this.#options.onOfferNozzle();
        this.#tracker.release({ x: this.#dog.position.x, y: 0, z: this.#dog.position.z });
        this.#dogPose = 'hunt';
        break;
      case CORRIDOR_CUES.waterRunning:
        this.#water = 0;
        break;
      // He whistles it back, and it stops being a threat for the rest of the
      // beat. The water and the gully are still there — see the beat's own
      // comment on WASH_PATIENCE: this is the game declining to hunt somebody who
      // is already lost, not the game solving the puzzle for her.
      case CORRIDOR_CUES.whistleItBack:
        this.#tracker.hold();
        this.#dogPose = 'heel';
        // Back to his heel means back to the beginning of the staging, not one
        // step short of it: leaving the station timer running would drop the
        // head again on the next fixed step and the whistle would mean nothing.
        this.#atStation = null;
        // **And it stays called off.** `#dogOnStage` is not enough on its own:
        // the handler's round keeps looping in the background whatever stage the
        // beat has reached, so his next dwell beside the wash-down bay would
        // re-arm the station timer and put the nose back down — minutes later,
        // possibly during `the-door`, with the player having watched him call it
        // off. Caught by independent review, not by a gate.
        this.#staging = 'done';
        // The dog is about to be snapped back to his heel from wherever it was
        // hunting. That is a teleport, not a walk; see `#stepDogSound`.
        this.#dogPrevious = null;
        this.#stepDistance = 0;
        break;
      // Nothing to stage. The handler's own round opens the door, twice, and the
      // player has already watched it happen.
      case CORRIDOR_CUES.theDoor:
        break;
      // Emitted by this class, handled by the capture timeline. Listed so the
      // switch covers the whole vocabulary rather than falling through.
      case CORRIDOR_CUES.dragged:
        break;
      case CORRIDOR_CUES.fadeOut:
        this.#fadeOverlay.setTarget(1);
        break;
      case CORRIDOR_CUES.end:
        break;
      default:
        break;
    }
  }

  /**
   * The fixed step. Everything the rest of the simulation can observe moves here.
   *
   * `stageId` is handed in rather than reached for, for the reason
   * `ottoDirector.ts` gives about her position: a director that can see the
   * sequencer can also drive it, and then the beat is no longer the thing that
   * decides what happens next.
   */
  step(dt: number, self: { x: number; y: number; z: number; grounded: boolean }, stageId: string | null): void {
    this.#stepCapture(dt);

    for (const patrol of this.patrols) patrol.step(dt);
    const handler = this.patrols[2].state;

    // The dog's stop is hung on the handler's dwell, not on a clock. See ARRIVAL.
    if (this.#dogOnStage && this.#staging === 'waiting' && this.#dogPose !== 'hunt') {
      if (handler.phase === 'dwelling' && handler.waypoint === ARRIVAL.station) {
        this.#atStation = (this.#atStation ?? 0) + dt;
      }
      if (this.#atStation !== null) {
        if (this.#atStation >= ARRIVAL.walksOnFor + ARRIVAL.noseDownAfter) {
          this.#dogPose = 'nose-down';
          this.#noseDown = true;
        } else if (this.#atStation >= ARRIVAL.walksOnFor) {
          this.#dogPose = 'stop';
        }
      }
    }

    if (this.#dogPose === 'hunt') {
      const state = this.#tracker.step(dt, this.#trail);
      this.#dog.position.set(state.x, 0, state.z);
      this.#dog.rotation.y = state.facing;
    } else if (this.#dogPose === 'heel') {
      // Only `heel` follows him. `stop` and `nose-down` mean the dog has stayed
      // where it was while the handler walked on, and that separation is the
      // whole of stage 5 — a dog that kept heeling while its nose was down would
      // be a dog reading the floor its owner is standing on.
      this.#heel(handler.x, handler.z, handler.facing);
    }
    // **Visibility does not follow the handler once the dog has left his heel.**
    // It did, and that is what hid the nose-down: the moment he parked — which
    // the old staging guaranteed, because it had just sent him out of the room —
    // the animal standing in the corridor with its head on the floor blinked out
    // and the stage played to nothing. Only a heeling dog is his to hide.
    this.#dog.visible = this.#dogOnStage && (this.#dogPose !== 'heel' || handler.phase !== 'parked');

    this.#stepDogSound(dt);
    this.#stepDoor(dt, self);
    // Written here rather than in `update`, and this is the exception that proves
    // the rule at the top of the file: the Rapier collider reads this panel's
    // world pose immediately after `step`, so leaving the rotation to the render
    // step put the solid one frame behind the angle the simulation had chosen.
    this.#doorHinge.rotation.y = this.#doorAngle + this.#matShiver();

    if (this.#water !== null) {
      this.#water += dt;
      this.#wetBox = this.#grownWetBox();
      this.#trail.wash(this.#wetBox);
    }

    // She lays a trail only from the stage the dog exists in. See TRAIL_STAGES.
    const laying = self.grounded && stageId !== null && TRAIL_STAGES.includes(stageId);
    this.#trail.step(dt, laying ? self : null);

    // `lost` is the tracker giving up after `giveUpSeconds` of casting with
    // nothing to smell. The flag latches: a dog that reacquires later has not
    // un-broken the trail she already broke, it has found a new one.
    if (this.#tracker.state.mode === 'lost') this.#trailBroken = true;
  }

  /**
   * Caught, or not. Called once a step, after `physics.step` and `stealth.step`.
   *
   * **Capture does not rewind the sequencer and must not learn how to.** The
   * stage she was in is still waiting on the flag she has not raised, so it
   * simply continues; the walk back from the near end is the cost. That is spec
   * §5.6 exactly — *no lives, no game over, the world just gets harder and she
   * keeps going* — and a rewind would re-emit entry cues that have already been
   * spent. It reads like an omission and it is a decision.
   */
  capture(
    snapshot: StealthSnapshot,
    self: { x: number; y: number; z: number },
    moveSelf: (spawn: typeof SERVICE_SPAWN) => void,
  ): void {
    // Not while the screen is already going dark: a second detection landing on a
    // player who cannot act is the unexplainable detection spec §5.1 forbids.
    if (this.#captureStage !== null || this.fade > CAPTURE.fadeFloor) return;

    const hunting = this.#tracker.state.mode === 'tracking' || this.#tracker.state.mode === 'casting';
    const caughtByDog = hunting
      && Math.hypot(this.#dog.position.x - self.x, this.#dog.position.z - self.z) <= DOG.catchRadius;
    const seen = snapshot.watchers.some((watcher) => watcher.state === 'detected');
    if (!caughtByDog && !seen) return;

    // The alarm is NOT reset and is not raised here either: `StealthSystem` put it
    // up on the detection itself, and that is the whole consequence.
    this.#options.onCue(CORRIDOR_CUES.dragged);
    this.#fadeOverlay.setTarget(1);
    this.#captureStage = 'out';
    this.#captureElapsed = 0;
    this.#captureRespawn = moveSelf;
  }

  /** The render step. Meshes catch up with decisions already made in `step`. */
  update(frameDt: number): void {
    // Clamped for the reason every director clamps: a tab that has been in the
    // background hands back one enormous frame, and a gait driven by it snaps.
    const dt = Number.isFinite(frameDt) ? Math.max(0, Math.min(0.1, frameDt)) : 0;
    this.#gaitClock += dt;
    this.patrols.forEach((patrol, index) => {
      const mesh = this.#workerMeshes[index];
      if (!mesh) return;
      const state = patrol.state;
      mesh.position.set(state.x, 0, state.z);
      mesh.rotation.y = state.facing;
      this.#animateWorker(index, state.x, state.z, state.phase, dt);
      // Parked is off-stage, through the door. `LatchDirector` hides its worker
      // the same way and for the same reason: the alternative is a man standing
      // in a wall for the twenty-four seconds between his rounds.
      mesh.visible = state.phase !== 'parked';
    });

    this.#animateDog();
    this.#fadeOverlay.update(dt);

    if (this.#wetBox !== null) {
      const box = this.#wetBox;
      this.#wetPatch.visible = true;
      this.#wetPatch.position.set((box.min.x + box.max.x) / 2, 0.005, (box.min.z + box.max.z) / 2);
      // The plane was built 1 x 1 and turned to face up, so its local X and Y are
      // the world's X and Z. Scaling rather than rebuilding geometry is what lets
      // the patch grow every frame for free.
      this.#wetPatch.scale.set(box.max.x - box.min.x, box.max.z - box.min.z, 1);
    }
  }

  dispose(): void {
    this.#fadeOverlay.dispose();
    for (const mesh of this.#workerMeshes) mesh.removeFromParent();
    this.#dog.removeFromParent();
    this.#doorLeaf.removeFromParent();
    this.#wetPatch.removeFromParent();
    disposeAsset(this.#dog);
    disposeAsset(this.#doorLeaf);
    disposeAsset(this.#wetPatch);
  }

  #stepCapture(dt: number): void {
    if (this.#captureStage === null) return;
    this.#captureElapsed += dt;
    if (this.#captureStage === 'out' && this.#captureElapsed >= CAPTURE.blackAt) {
      this.#captureRespawn?.(SERVICE_SPAWN);
      this.#tracker.hold();
      this.#dogPose = 'heel';
      // `#dogOnStage` and `#staging` are deliberately NOT cleared. Being caught
      // does not delete the dog from the beat — spec §5.6 is that the world gets
      // harder and she keeps going — so it goes back to his heel and the staging
      // replays when the handler next reaches his station, which the interrupt
      // below starts him around the round to do.
      this.#atStation = null;
      // She has just been carried the length of the corridor and the dog has been
      // put back at his heel. Neither of those is a walk; see `#stepDogSound`.
      this.#dogPrevious = null;
      this.#stepDistance = 0;
      // `interrupt()` and not a reset to park, because `Worker` has no reset and
      // `src/systems/worker.ts` is not this beat's file to change. What it means
      // is that the three men *walk* back to the far door rather than vanishing
      // from it — which is arguably the better read anyway, since she has just
      // been carried past them. Filed against the beat that needs an instant one.
      for (const patrol of this.patrols) patrol.interrupt();
      this.#fadeOverlay.setTarget(0);
      this.#captureStage = 'in';
      this.#captureElapsed = 0;
    } else if (this.#captureStage === 'in' && this.#captureElapsed >= CAPTURE.backAt) {
      this.#captureStage = null;
    }
  }

  /**
   * The dog, at the handler's heel.
   *
   * Facing follows `stealth.ts`'s convention — 0 faces +Z, increasing toward +X —
   * so forward is `(sin f, cos f)`. With Y up and that convention **+X is his
   * left**, which makes left `(cos f, −sin f)`. The first version of this had the
   * two coefficients swapped, which put the dog out in front of him and half a
   * metre to the wrong side: it walked backwards down the corridor at heel and
   * nothing in the numbers said so.
   */
  #heel(x: number, z: number, facing: number): void {
    const forwardX = Math.sin(facing);
    const forwardZ = Math.cos(facing);
    this.#dog.position.set(
      x - forwardX * DOG.heelBehind + forwardZ * DOG.heelLeft,
      0,
      z - forwardZ * DOG.heelBehind - forwardX * DOG.heelLeft,
    );
    this.#dog.rotation.y = facing;
  }

  /**
   * What the dog is heard doing, in the fixed step that decided it.
   *
   * Both events are emitted from `step` rather than `update` and that is not a
   * detail: a claw tick is the player's only information about a hunter she
   * cannot see, so it has to be laid down by the same clock that moved the
   * animal. Driven from the render step it would drift against the position it
   * is supposed to describe, and drift the most exactly when the frame rate
   * drops — which in this room is when three men and a dog are on screen.
   */
  #stepDogSound(dt: number): void {
    if (!this.#dog.visible) {
      // Not merely "do not play": reset, so a dog that walks off stage and comes
      // back does not tick the instant it reappears on a distance it accumulated
      // while nobody could see it.
      this.#dogPrevious = null;
      this.#stepDistance = 0;
      return;
    }

    const here = { x: this.#dog.position.x, z: this.#dog.position.z };
    const previous = this.#dogPrevious;
    this.#dogPrevious = here;
    if (previous !== null) {
      const moved = Math.hypot(here.x - previous.x, here.z - previous.z);
      // **A teleport is not a walk.** The dog is snapped rather than walked in
      // two places — the whistle and a capture both put it straight back at the
      // handler's heel — and a snap of several metres landing in a distance
      // accumulator drains as a burst of claw ticks all at one point on the
      // floor. Threshold off the dog's own top speed rather than a new constant,
      // so it stays right if the animal is ever made faster: four steps' worth of
      // travel in a single fixed step cannot happen on foot.
      if (moved > DOG.speed * dt * 4) {
        this.#stepDistance = 0;
      } else {
        this.#stepDistance += moved;
        while (this.#stepDistance >= DOG.stride) {
          this.#stepDistance -= DOG.stride;
          this.#options.onDogStep({ x: here.x, y: 0.05, z: here.z });
        }
      }
    }

    if (this.#dogPose === 'nose-down') {
      // The first snuff lands on the step the head goes down, not a cast period
      // later: the sound is what makes the pose legible, and a silent second and
      // a half is long enough for the player to stop looking.
      if (this.#sinceCast === 0 || this.#sinceCast >= DOG.castEvery) {
        this.#options.onDogCast({ x: here.x, y: 0.12, z: here.z });
        this.#sinceCast = dt;
      } else {
        this.#sinceCast += dt;
      }
    } else {
      this.#sinceCast = 0;
    }
  }

  /**
   * The mat, and the door it opens.
   *
   * **Only the three men load this mat.** `DOOR.boots` records what it wants and
   * is deliberately not used in a computation: she is a weaner, she does not
   * weigh it, no amount of rooting will change that, and there is no code path by
   * which she could. That is not an omission to be fixed — it is the beat's whole
   * thesis, that she goes through on somebody else's timing.
   */
  #stepDoor(dt: number, self: { x: number; z: number }): void {
    // **She cannot open it, and the mat must still say so.** Until session 76
    // she could stand on the most prominent object at the end of the corridor
    // and nothing in the world noticed (playthrough audit row 12). Her weight
    // does what a weaner's weight does on a mat built for boots: the switch
    // under the rubber half-makes, the latch knocks in its keeper, the leaf
    // shivers and stays shut. That says "a mechanism, and not enough of you"
    // without a prompt, a highlight or a hint, which the plan forbids. It
    // loads nothing: `loaded` below still reads the men and only the men.
    const onMat = Math.abs(self.x - MAT_AT.x) <= DOOR.matHalf.x && Math.abs(self.z - MAT_AT.z) <= DOOR.matHalf.z;
    if (this.#sinceMatTry !== null) this.#sinceMatTry += dt;
    if (onMat && !this.#herOnMat && this.#doorAngle === 0 && (this.#sinceMatTry === null || this.#sinceMatTry >= MAT_SHIVER.again)) {
      this.#sinceMatTry = 0;
      this.#matTries += 1;
      this.#options.onMatTry?.({ x: DOOR_AT.x, y: 1.0, z: DOOR_AT.z });
    }
    this.#herOnMat = onMat;

    const loaded = this.patrols.some((patrol) => {
      const state = patrol.state;
      return state.phase !== 'parked'
        && Math.abs(state.x - MAT_AT.x) <= DOOR.matHalf.x
        && Math.abs(state.z - MAT_AT.z) <= DOOR.matHalf.z;
    });
    // Held open while loaded, then counting down. `holdAfterRelease` is the three
    // seconds stage 7 is built on, and it starts the instant he steps off.
    this.#matHold = loaded ? DOOR.holdAfterRelease : Math.max(0, this.#matHold - dt);

    // Linear, not exponential, so `swingSeconds` means the number it says. An
    // exponential approach never arrives, and a door that is asymptotically shut
    // is a door with a gap in it.
    const target = this.#matHold > 0 ? DOOR.openAngle : 0;
    const perSecond = DOOR.openAngle / DOOR.swingSeconds;
    const step = perSecond * dt;
    this.#doorAngle = this.#doorAngle < target
      ? Math.min(target, this.#doorAngle + step)
      : Math.max(target, this.#doorAngle - step);
  }

  /** The leaf's shiver when her weight half-makes the mat: radians, decaying. Zero once it has died away. */
  #matShiver(): number {
    const t = this.#sinceMatTry;
    if (t === null || t >= MAT_SHIVER.seconds || this.#doorAngle !== 0) return 0;
    return MAT_SHIVER.angle * Math.exp(-t * MAT_SHIVER.decay) * Math.abs(Math.sin(t * MAT_SHIVER.rate));
  }

  /**
   * The wet patch, growing from the gully toward its final extent.
   *
   * Each edge is interpolated independently from a point at the nozzle, so the
   * water spreads out of the bay and across the corridor rather than appearing as
   * a rectangle that scales. `WATER.full` reaches the far wall of the long leg on
   * purpose: the lane she has to cross is the lane that has to get wet.
   */
  #grownWetBox(): Aabb {
    const spread = Math.min(1, (this.#water ?? 0) / WATER.spreadSeconds);
    const lerp = (from: number, to: number): number => from + (to - from) * spread;
    return {
      min: { x: lerp(WATER.source.x, WATER.full.minX), y: -0.01, z: lerp(WATER.source.z, WATER.full.minZ) },
      max: { x: lerp(WATER.source.x, WATER.full.maxX), y: 0.01, z: lerp(WATER.source.z, WATER.full.maxZ) },
    };
  }

  #animateDog(): void {
    // The pose vocabulary stays here and the arithmetic does not: this dog walks
    // at heel and while hunting, drops its nose on a line and while casting, and
    // **never sways its tail** — it is at a handler's heel indoors, not
    // quartering a field. `src/anim/dogPose.ts` holds why each of those reads.
    applyDogPose(this.#dogParts, {
      walking: this.#dogPose === 'heel' || this.#dogPose === 'hunt',
      noseDown: this.#dogPose === 'hunt',
      casting: this.#dogPose === 'nose-down',
      tail: false,
      clock: this.#gaitClock,
    });
  }

  #animateWorker(index: number, x: number, z: number, phase: string, dt: number): void {
    const parts = this.#workerParts[index];
    const patrol = this.patrols[index];
    if (!parts || !patrol) return;
    const previous = this.#workerPrevious[index] ?? null;
    const distance = previous === null ? 0 : Math.hypot(x - previous.x, z - previous.z);
    this.#workerPrevious[index] = { x, z };
    const distanceTravelled = this.#workerDistance[index] ?? 0;
    if (phase === 'walking' || phase === 'returning') this.#workerDistance[index] = distanceTravelled + distance;

    const walking = (phase === 'walking' || phase === 'returning') && distance > 0.0001;
    const definition = PATROL_DEFINITIONS[index];
    if (!definition) return;
    const swing = walking ? Math.sin(((this.#workerDistance[index] ?? 0) / definition.route.stride) * Math.PI) : 0;
    const settle = 1 - Math.exp(-8 * dt);
    const pose = (node: THREE.Object3D, target: number): void => {
      // 8/s settles a stopped limb within a few frames without the vertical snap
      // that made a halted full-height figure look worse than a sliding one.
      node.rotation.x += (target - node.rotation.x) * settle;
    };
    pose(parts.hips[0], swing * 0.42);
    pose(parts.hips[1], -swing * 0.42);
    pose(parts.shoulders[0], -swing * 0.26);
    pose(parts.shoulders[1], swing * 0.26);
  }
}
